import { patchHostedBusinessVaultMutation } from '@onefootprint/fetch-hosted';
import type { FieldError } from '@onefootprint/hooks';
import type { HostedBusinessOwner } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useVaultMutationError } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
import type { ManageBosFormData, NewBusinessOwner } from '../manage-business-owners.types';
import { sumTotalOwnershipStake } from '../utils/manage-bos-utils';
import { BosForm } from './bos-form';
import { ConfirmMissingBoDialog } from './confirm-missing-bo-dialog';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'edit-bos-form'
});
export type EditBosFormProps = {
  authToken: string;
  existingBos: HostedBusinessOwner[];
  minimumBoStake: number;
  onDone: () => void;
  defaultFormValues: NewBusinessOwner[];
  allowDuplicateContactInfo: boolean;
  formId?: string;
  onSubmit: (operations: {
    updateOrCreateOperations: Array<{
      uuid: string;
      data: Partial<Record<string, string | undefined>>;
      ownershipStake?: number;
    }>;
    deleteOperations: string[];
  }) => Promise<void>;
};
export const EditBosForm = ({
  authToken,
  existingBos,
  onDone,
  defaultFormValues,
  allowDuplicateContactInfo,
  minimumBoStake,
  onSubmit,
  formId
}: EditBosFormProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dialogResolver = useRef<((shouldContinue: boolean) => void) | undefined>(undefined);
  const {
    mutateAsync: saveNote,
    isPending: isDialogLoading
  } = useMutation(patchHostedBusinessVaultMutation({
    headers: {
      'X-Fp-Authorization': authToken
    }
  }));
  const handleVaultError = useVaultMutationError();
  const missingBosConfirmationThreshold = 101 - minimumBoStake;
  const showConfirmationModal = () => new Promise(resolve => {
    dialogResolver.current = resolve;
    setIsDialogOpen(true);
  });
  const closeDialog = (shouldContinue: boolean) => {
    dialogResolver.current?.(shouldContinue);
    setIsDialogOpen(false);
  };
  const handleDialogClose = () => closeDialog(false);
  const handleDialogSubmit = async (note: string) => {
    try {
      const body = {
        'business.beneficial_owner_explanation_message': note
      };
      logInfo('Saving BO explanation note vault mutation', {
        vaultKeys: Object.keys(body).join(',')
      });
      await saveNote({
        body
      });
    } catch (error) {
      logWarn('Error sending business stake explanation message', error);
    } finally {
      closeDialog(true);
    }
  };
  const handleBosFormSubmit = async ({
    bos,
    bosToDelete
  }: ManageBosFormData, onFieldError: (errors: FieldError[]) => void) => {
    logInfo('Submitting business owners form', {
      bosCount: bos.length,
      bosToDeleteCount: bosToDelete.length
    });
    const totalOwnershipStake = sumTotalOwnershipStake(existingBos, {
      bos,
      bosToDelete
    });
    if (totalOwnershipStake < missingBosConfirmationThreshold) {
      logInfo('Ownership stake below threshold, showing confirmation modal', {
        totalOwnershipStake,
        threshold: missingBosConfirmationThreshold
      });
      const shouldContinue_0 = await showConfirmationModal();
      if (!shouldContinue_0) {
        logInfo('User cancelled submission at confirmation modal');
        return;
      }
    }
    if (!existingBos.length || !authToken) {
      throw new Error('Business owners data or authentication token is missing.');
    }
    const updateOrCreateOperations = bos.map(({
      uuid,
      firstName,
      lastName,
      email,
      phoneNumber,
      ownershipStake
    }) => {
      const existingBo = existingBos.find(bo => bo.uuid === uuid);
      const isMutable = !existingBo || existingBo?.isMutable;
      // Backend rejects data updates for BOs that have a linked user.
      // For these BOs, name updates go through the user vault mutation (handled by syncAuthedUserVault).
      const hasLinkedUser = existingBo?.hasLinkedUser === true;
      const shouldSendData = isMutable && !hasLinkedUser;
      const data = {
        'id.first_name': firstName,
        'id.last_name': lastName,
        'id.email': email,
        'id.phone_number': phoneNumber
      };
      return {
        uuid,
        data: shouldSendData ? data : {},
        ownershipStake
      };
    });
    try {
      logInfo('Executing business owner operations', {
        updateOrCreateCount: updateOrCreateOperations.length,
        deleteCount: bosToDelete.length
      });
      await onSubmit({
        updateOrCreateOperations,
        deleteOperations: bosToDelete
      });
      logInfo('Business owner operations completed successfully');
      onDone();
    } catch (error_0) {
      logError('Error submitting business owner operations', error_0);
      handleVaultError(error_0, onFieldError);
    }
  };
  return <>
      <BosForm existingBos={existingBos} onSubmit={handleBosFormSubmit} defaultFormValues={defaultFormValues} allowDuplicateContactInfo={allowDuplicateContactInfo} formId={formId} />
      <ConfirmMissingBoDialog minimumBoStake={minimumBoStake} isOpen={isDialogOpen} onClose={handleDialogClose} onSubmit={handleDialogSubmit} isLoading={isDialogLoading} />
    </>;
};